import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";

export interface RopBuildSiteOption {
  route: "internal" | "cm";
  supplier_id: number | null;
  supplier_name: string | null;
  location_id: number;
  location_name: string;
}

export interface RopBuildMaterialLine {
  component_sku: string;
  description: string | null;
  quantity_per_build: number;
  quantity_required: number;
  on_hand: number;
  remaining: number;
  short: boolean;
}

export interface RopBuildPreview {
  sku: string;
  quantity: number;
  route: "internal" | "cm";
  location_id: number;
  location_name: string;
  lines: RopBuildMaterialLine[];
}

export interface RopBuildCreateResult {
  route: "internal" | "cm";
  work_order_id: number | null;
  work_order_number: string | null;
  purchase_order_id: number | null;
  purchase_order_number: string | null;
}

interface SafetyStockBuildChoice {
  sku: string;
  defaultQuantity: number;
}

function formatQty(value: number): string {
  if (!Number.isFinite(value)) return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export function SafetyStockBuildWorkOrderModal(props: {
  rows: SafetyStockBuildChoice[];
  onClose: () => void;
  onCreated: (result: RopBuildCreateResult) => void;
}): JSX.Element {
  const [sku, setSku] = useState(props.rows[0]?.sku ?? "");
  const [quantityText, setQuantityText] = useState(String(props.rows[0]?.defaultQuantity ?? 1));
  const [routeKey, setRouteKey] = useState("internal");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sitesQuery = useQuery<{ sites: RopBuildSiteOption[] }, ApiError>({
    queryKey: ["mrp", "safety-stock-build-sites"],
    queryFn: () => apiFetch<{ sites: RopBuildSiteOption[] }>("/api/mrp/safety-stock-alerts/build-sites"),
  });

  const selectedSite = useMemo(() => {
    const sites = sitesQuery.data?.sites ?? [];
    if (routeKey === "internal") return sites.find((site) => site.route === "internal") ?? null;
    const supplierId = Number(routeKey.replace(/^cm:/, ""));
    return sites.find((site) => site.route === "cm" && site.supplier_id === supplierId) ?? null;
  }, [routeKey, sitesQuery.data?.sites]);

  const quantity = Number(quantityText);
  const previewQuery = useQuery<{ preview: RopBuildPreview }, ApiError>({
    queryKey: ["mrp", "safety-stock-build-preview", sku, quantity, routeKey],
    enabled: sku !== "" && Number.isFinite(quantity) && quantity > 0 && selectedSite != null,
    queryFn: () => {
      const params = new URLSearchParams({
        sku,
        quantity: String(quantity),
        route: selectedSite?.route ?? "internal",
      });
      if (selectedSite?.supplier_id != null) params.set("supplier_id", String(selectedSite.supplier_id));
      return apiFetch<{ preview: RopBuildPreview }>(
        `/api/mrp/safety-stock-alerts/build-preview?${params.toString()}`,
      );
    },
  });

  async function submit(): Promise<void> {
    if (selectedSite == null || !Number.isFinite(quantity) || quantity <= 0) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const created = await apiFetch<RopBuildCreateResult>("/api/mrp/safety-stock-alerts/build", {
        method: "POST",
        body: {
          sku,
          quantity,
          route: selectedSite.route,
          supplier_id: selectedSite.supplier_id,
        },
      });
      props.onCreated(created);
    } catch (error) {
      setErrorMessage(error instanceof ApiError ? error.message : "Could not create this build.");
      setSubmitting(false);
    }
  }

  const lines = previewQuery.data?.preview.lines ?? [];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="safety-stock-build-dialog-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 id="safety-stock-build-dialog-title" className="text-lg font-semibold text-slate-900">
            Create work order
          </h2>
          <p className="mt-1 text-xs text-slate-600">
            Material is shown at the place the work will happen. In-house uses SGS. A contract manufacturer uses that
            supplier&apos;s site, not the site that is short. A red row means this quantity needs more of that part than
            is on hand there. You can still create the order.
          </p>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {props.rows.length > 1 ? (
            <div>
              <Label htmlFor="rop-build-sku">SKU</Label>
              <select
                id="rop-build-sku"
                className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={sku}
                onChange={(event) => {
                  const nextSku = event.target.value;
                  setSku(nextSku);
                  const match = props.rows.find((row) => row.sku === nextSku);
                  if (match != null) {
                    setQuantityText(String(match.defaultQuantity > 0 ? match.defaultQuantity : 1));
                  }
                }}
              >
                {props.rows.map((row) => (
                  <option key={row.sku} value={row.sku}>
                    {row.sku}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p className="font-mono text-sm text-slate-900">{sku}</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="rop-build-qty">Quantity</Label>
              <Input
                id="rop-build-qty"
                className="mt-1"
                type="number"
                min={1}
                value={quantityText}
                onChange={(event) => setQuantityText(event.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">Starts at the amount that brings net stock back to reorder-to.</p>
            </div>
            <div>
              <Label htmlFor="rop-build-route">Where to build</Label>
              <select
                id="rop-build-route"
                className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={routeKey}
                onChange={(event) => setRouteKey(event.target.value)}
              >
                <option value="internal">In-house at SGS</option>
                {(sitesQuery.data?.sites ?? [])
                  .filter((site) => site.route === "cm")
                  .map((site) => (
                    <option key={site.supplier_id} value={`cm:${site.supplier_id}`}>
                      {site.supplier_name} ({site.location_name})
                    </option>
                  ))}
              </select>
            </div>
          </div>
          {previewQuery.isLoading ? <p className="text-sm text-slate-600">Loading bill of materials…</p> : null}
          {previewQuery.error ? (
            <p className="text-sm text-rose-700">{previewQuery.error.message}</p>
          ) : null}
          {lines.length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2">Component</th>
                    <th className="px-2 py-2 text-right">Needed</th>
                    <th className="px-2 py-2 text-right">On hand</th>
                    <th className="px-2 py-2 text-right">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.component_sku} className={line.short ? "bg-rose-50 text-rose-900" : "text-slate-800"}>
                      <td className="px-2 py-2">
                        <div className="font-mono text-xs">{line.component_sku}</div>
                        {line.description ? <div className="text-xs text-slate-600">{line.description}</div> : null}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{formatQty(line.quantity_required)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{formatQty(line.on_hand)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{formatQty(line.remaining)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {errorMessage ? <p className="text-sm text-rose-700">{errorMessage}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <Button type="button" variant="secondary" onClick={props.onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={submitting || selectedSite == null} isLoading={submitting}>
            {selectedSite?.route === "cm" ? "Create CM purchase order" : "Create in-house work order"}
          </Button>
        </div>
      </div>
    </div>
  );
}
